import { memo, useCallback, useMemo, useRef, useState } from 'react';

import { EditIcon, SmallDropdownArrow } from '@/assets/icons';
import { DeleteIcon } from '@/assets/images';
import { ROOT_FOLDER_PATH } from '@/common/constants';
import { DataRoomClientFile } from '@/common/types';
import { createFileInstanceId } from '@/contexts/overview/dataroom/fileInstanceUtils';
import { useContextMenu } from '@/contexts/overview/dataroom/useContextMenu';
import { useDataRoom } from '@/contexts/overview/dataroom/utils';
import { MarveriIcon } from '@/pages/overview/common/MarveriIcon';
import { useOverview } from '@/pages/overview/common/utils';
import { ContextMenu } from '@/pages/overview/dataroom/content/common/ContextMenu';
import { DraggableDataRoomFolder } from '@/pages/overview/dataroom/content/common/DraggableDataRoomFolder';
import { Droppable } from '@/pages/overview/dataroom/content/common/Droppable';
import {
  type FolderPath,
  getFileCurrentPath,
  isRootPath,
  isTrashPath,
} from '@/pages/overview/dataroom/content/common/folderUtils';
import { RenameListItemForm } from '@/pages/overview/dataroom/content/common/RenameListItemForm';
import { DataRoomFileList } from '@/pages/overview/dataroom/content/file-list/DataRoomFileList';
import { trpcClient } from '@/utils/trpc';

interface OrganizedViewLayoutProps {
  path: string;
  files: DataRoomClientFile[];
  dataRoomInteraction: boolean | undefined;
}

const OrganizedViewLayout = ({ path, files, dataRoomInteraction }: OrganizedViewLayoutProps) => {
  const isTrashFolder = useMemo(() => {
    return isTrashPath(path as FolderPath);
  }, [path]);

  const isRootFolder = useMemo(() => {
    return isRootPath(path as FolderPath);
  }, [path]);

  const [depth, setDepth] = useState<number>(0);
  const [isFolderRenameActive, setIsFolderRenameActive] = useState<boolean>(false);
  const [hoveredFolder, setHoveredFolder] = useState('');
  const [focusedFolder, setFocusedFolder] = useState<string>('');

  const renameInputRef = useRef<HTMLInputElement>(null);
  const { isContextMenuVisible, setIsContextMenuVisible, position, setPosition } = useContextMenu();

  const {
    matter: { id: clientMatterId, dataRoomFiles: allFiles },
    isUserCurrentEditor,
  } = useOverview();

  const {
    isFolderOpenByPath,
    setIsFolderOpenByPath,
    setCheckedFiles,
    setCheckedFileInstances,
    isFolderChecked,
    setIsFolderChecked,
    updateRenameFormStatus,
    foldersByPaths,
    pathOrganizedDocuments,
  } = useDataRoom();

  const isOpen = useMemo(() => {
    const folders = path.split('/').slice(1);
    const areFoldersOpen = folders.map((_folder, index) => {
      return isFolderOpenByPath['/' + folders.slice(0, index + 1).join('/')];
    });
    return areFoldersOpen.every((val) => val);
  }, [isFolderOpenByPath, path]);

  const isVisible = useMemo(() => {
    const parentFolders = path.split('/').slice(1, -1);
    if (parentFolders.length < 1) {
      return true;
    } else {
      const areFoldersOpen = parentFolders.map((_folder, index) => {
        return isFolderOpenByPath['/' + parentFolders.slice(0, index + 1).join('/')];
      });
      return areFoldersOpen.every((val) => val);
    }
  }, [isFolderOpenByPath, path]);

  const displayPath = useMemo(() => {
    // TODO refactor to use folderUtils
    const folders = path.split('/');
    if (folders.length === 2 && folders[1] === 'root') {
      setDepth(0);
      return '';
    }
    setDepth(folders.length - 2);
    const folder = foldersByPaths.get(path)?.[0];
    return folder?.displayName || folder?.name || folders[folders.length - 1];
  }, [path, foldersByPaths]);

  const clickRenameFolder = useCallback((e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setIsFolderRenameActive(true);
  }, []);

  const renameFolder = useCallback(
    async (newName: string) => {
      setIsFolderRenameActive(false);
      if (newName.length > 0) {
        const folder = foldersByPaths.get(path)?.[0];
        if (folder) {
          await trpcClient.dataRoom.updateFolderName.mutate({
            clientMatterId,
            folderKey: folder.key,
            newName,
          });
        }
      }
    },
    [clientMatterId, path, foldersByPaths],
  );

  const widthClass = useMemo(() => {
    if (depth < 2) {
      return { width: '100%' };
    } else {
      return { width: '100%', marginLeft: `${(depth - 1) * 32}px` };
    }
  }, [depth]);

  const toggleCollapse = useCallback(() => {
    if (!isFolderRenameActive) {
      setIsFolderOpenByPath((map) => {
        const newMap = { ...map };
        newMap[path] = !newMap[path];
        newMap['/'] = true;
        const storedOpenMapJson = JSON.stringify(newMap);
        localStorage.setItem(`folderOpenMapRefactor-${clientMatterId}`, storedOpenMapJson);
        return newMap;
      });
      updateRenameFormStatus(false);
    }
  }, [clientMatterId, isFolderRenameActive, path, setIsFolderOpenByPath, updateRenameFormStatus]);

  const onFolderCheck = useCallback(() => {
    if (!isOpen && !isFolderChecked[path]) toggleCollapse();

    // Get all subfolder paths first
    const subfolderPaths = Array.from(foldersByPaths.keys()).filter((folderPath) => {
      // Skip the current folder and trash folders
      if (folderPath === path || folderPath.startsWith('/root/~trash')) {
        return false;
      }
      // A folder is a subfolder if it starts with the current path (plus a slash)
      const currentPathWithSlash = path.endsWith('/') ? path : `${path}/`;
      return folderPath.startsWith(currentPathWithSlash);
    });

    // Get files from current folder and all subfolders
    const filesInFolder = [
      ...(pathOrganizedDocuments.get(path) || []),
      ...subfolderPaths.flatMap((subfolderPath) => pathOrganizedDocuments.get(subfolderPath) || []),
    ];

    const isChecked = !isFolderChecked[path];

    // First update folder states
    setIsFolderChecked((prevState) => {
      const newState = { ...prevState };

      // Update the checked state for this folder
      newState[path] = isChecked;

      // Update all subfolder states
      subfolderPaths.forEach((subfolderPath) => {
        newState[subfolderPath] = isChecked;
      });

      return newState;
    });

    // Then update file selection
    if (isChecked) {
      // Add all files in this folder and subfolders
      setCheckedFiles((prevFiles) => {
        const newFiles = [...prevFiles];
        filesInFolder.forEach((file) => {
          if (!newFiles.some((f) => f.name === file.name)) {
            newFiles.push(file);
          }
        });

        return newFiles;
      });

      // Add FileInstance objects for all files in the current folder and subfolders
      setCheckedFileInstances((prevInstances) => {
        const newInstances = [...prevInstances];
        const pathsToCheck = [path, ...subfolderPaths];

        pathsToCheck.forEach((folderPath) => {
          const filesInThisFolder = pathOrganizedDocuments.get(folderPath) || [];
          filesInThisFolder.forEach((file) => {
            const instanceId = createFileInstanceId(file.id, folderPath);
            // Only add if this specific instance doesn't already exist
            if (!newInstances.some((instance) => instance.instanceId === instanceId)) {
              newInstances.push({
                file,
                folderPath,
                instanceId,
              });
            }
          });
        });

        return newInstances;
      });
    } else {
      // Remove all files in this folder and subfolders
      setCheckedFiles((prevFiles) => {
        const filteredFiles = prevFiles.filter(
          (file) => !filesInFolder.some((f) => f.name === file.name),
        );
        return filteredFiles;
      });

      // Remove FileInstance objects for the current folder and subfolders
      setCheckedFileInstances((prevInstances) => {
        const pathsToRemove = [path, ...subfolderPaths];
        return prevInstances.filter((instance) => !pathsToRemove.includes(instance.folderPath));
      });
    }
  }, [
    isFolderChecked,
    isOpen,
    path,
    setCheckedFiles,
    setCheckedFileInstances,
    setIsFolderChecked,
    toggleCollapse,
    foldersByPaths,
    pathOrganizedDocuments,
  ]);

  const checkForSubfolders = useMemo(() => {
    const filesInSubFolders = allFiles.filter((f: DataRoomClientFile) => {
      const currentPath = getFileCurrentPath(f, foldersByPaths);
      return currentPath.startsWith(path) && currentPath.length > path.length;
    });
    return filesInSubFolders.length > 0;
  }, [path, allFiles, foldersByPaths]);

  const deleteFolder = useCallback(
    async (e: React.MouseEvent<HTMLElement>) => {
      e.stopPropagation();

      // First move any files in the folder to trash
      const filesInFolder = allFiles.filter((file) => {
        const currentPath = getFileCurrentPath(file, foldersByPaths);
        return currentPath.startsWith(path);
      });
      if (filesInFolder.length > 0) {
        // Build fileMoves with known source folder (current folder being deleted)
        const currentFolderKey = path.split('/').pop() || '';
        const fileMoves = filesInFolder.map((file) => ({
          fileName: file.name,
          sourceFolderKey: currentFolderKey,
        }));

        await trpcClient.dataRoom.bulkMoveFilesToFolder.mutate({
          clientMatterId,
          fileMoves,
          targetFolderKey: '~trash',
          targetFolderType: 'default',
        });
      }

      // Then delete the folder
      await trpcClient.dataRoom.deleteFolder.mutate({
        clientMatterId,
        folderKey: path.split('/').pop() || '',
      });
    },
    [allFiles, clientMatterId, path, foldersByPaths],
  );

  const handleRightClick = (e: React.MouseEvent, folder: string) => {
    //temp fix to prevent context menu from appearing (remove below return after testing)
    return;
    if (!dataRoomInteraction || folder === '/~Trash') return;
    e.preventDefault();

    setFocusedFolder(folder);
    setIsContextMenuVisible(true);
    setPosition({
      x: e.clientX,
      y: e.clientY,
    });
  };

  const handleMouseLeaveFolder = () => {
    setIsContextMenuVisible(false);
  };

  return (
    <>
      {isVisible && (
        <div className={`mb-[8px] flex`} data-testid="data-room-folder">
          <div
            style={widthClass}
            id={path}
            onContextMenu={(e) => handleRightClick(e, path)}
            onMouseLeave={handleMouseLeaveFolder}
          >
            {!isRootFolder ? (
              <DraggableDataRoomFolder
                draggableId={path}
                currentDroppableId={path}
                disabled={
                  isFolderRenameActive || isRootFolder || isTrashFolder || !isUserCurrentEditor
                }
                data-testid="draggable-data-room-folder"
                isOpen={isOpen}
                isFolderChecked={isFolderChecked[path]}
                onMouseEnter={() => setHoveredFolder(path)}
                onMouseLeave={() => setHoveredFolder('')}
              >
                {(!isTrashFolder && hoveredFolder === path) || isFolderChecked[path] ? (
                  <div
                    data-testid="data-room-folder-checkbox"
                    className="flex items-center justify-center"
                  >
                    <input
                      checked={isFolderChecked[path]}
                      type="checkbox"
                      onChange={onFolderCheck}
                      className={`size-[12px] cursor-pointer appearance-none rounded-[2px] border border-marveri-white  bg-contain bg-center bg-no-repeat checked:bg-[url('@/assets/icons/folder-collapse-icon.svg')]`}
                    />
                  </div>
                ) : (
                  <div className="flex items-center justify-center">
                    <div className="size-[12px] border border-transparent"></div>
                  </div>
                )}
                <div
                  className={`group relative flex h-[50px] w-full cursor-pointer items-center gap-[11px] pl-3`}
                  onClick={toggleCollapse}
                >
                  <div data-testid="folder-drop-down-arrow">
                    {isOpen ? (
                      <img
                        src={SmallDropdownArrow}
                        alt="arrow-down"
                        className="size-[8px] rotate-90"
                      />
                    ) : (
                      <img src={SmallDropdownArrow} alt="arrow-down" className="size-[8px]" />
                    )}
                  </div>
                  <div className="flex size-full flex-row justify-between">
                    <div className="flex w-full items-center" data-testid="data-room-folder-title">
                      {isFolderRenameActive ? (
                        <RenameListItemForm
                          currentName={displayPath}
                          onSubmit={(newName) => renameFolder(newName)}
                          onClose={() => setIsFolderRenameActive(false)}
                          renameInputRef={renameInputRef}
                        />
                      ) : (
                        <span className="items-start text-[14px] font-medium">
                          {isTrashFolder ? (
                            <MarveriIcon
                              icon={DeleteIcon}
                              iconStyle="select-none shrink-0"
                              iconType="list-main"
                            />
                          ) : (
                            displayPath
                          )}
                        </span>
                      )}
                      {path !== ROOT_FOLDER_PATH &&
                        !isTrashFolder &&
                        !isFolderRenameActive &&
                        dataRoomInteraction &&
                        !path.startsWith('/root/~trash') && (
                          <div
                            className="flex gap-[16px] pl-4 opacity-0 group-hover:opacity-100"
                            data-testid="data-room-folder-rename-buttons"
                          >
                            <div className="flex">
                              <MarveriIcon
                                icon={EditIcon}
                                onClick={clickRenameFolder}
                                iconStyle="select-none shrink-0 translate-y-[2px]"
                                iconType="list-main"
                                disabled={!isUserCurrentEditor}
                              />
                            </div>
                            <MarveriIcon
                              icon={DeleteIcon}
                              iconType="list-main"
                              iconStyle="absolute right-0"
                              onClick={deleteFolder}
                            />
                          </div>
                        )}
                    </div>
                  </div>
                </div>
                {isContextMenuVisible && (
                  <ContextMenu
                    focusedFile={undefined}
                    focusedFolder={focusedFolder}
                    folderInteraction={true}
                    xPosition={position.x}
                    yPosition={position.y}
                  />
                )}
              </DraggableDataRoomFolder>
            ) : (
              <></>
            )}
            {isOpen ? (
              <div>
                <DataRoomFileList
                  fileList={files}
                  dataRoomInteraction={dataRoomInteraction}
                  folderPath={path}
                  hasSubFolders={checkForSubfolders}
                />
              </div>
            ) : (
              <></>
            )}
          </div>
        </div>
      )}
    </>
  );
};

interface OrganizedViewProps {
  dataRoomInteraction?: boolean;
}

export const OrganizedView = memo(function OrganizedView({
  dataRoomInteraction = true,
}: OrganizedViewProps) {
  const {
    dataRoomFiles,
    filteredDataRoomFiles,
    pathOrganizedDocuments,
    filteredPathOrganizedDocuments,
    foldersByPaths,
  } = useDataRoom();

  const localDocumentsMap = useMemo(() => {
    if (filteredDataRoomFiles.length < dataRoomFiles.length) {
      return filteredPathOrganizedDocuments;
    } else {
      return pathOrganizedDocuments;
    }
  }, [
    pathOrganizedDocuments,
    filteredPathOrganizedDocuments,
    dataRoomFiles,
    filteredDataRoomFiles,
  ]);

  const rootFolderVisible = useMemo(() => {
    return foldersByPaths.has(ROOT_FOLDER_PATH);
  }, [foldersByPaths]);

  const rootFolderEmpty = useMemo(() => {
    return (
      rootFolderVisible &&
      (!localDocumentsMap.get(ROOT_FOLDER_PATH) ||
        localDocumentsMap.get(ROOT_FOLDER_PATH)?.length === 0)
    );
  }, [localDocumentsMap, rootFolderVisible]);

  const adjustedFiles = useMemo(() => {
    if (rootFolderEmpty) {
      const newFiles = new Map(localDocumentsMap);
      newFiles.delete(ROOT_FOLDER_PATH);
      return newFiles;
    } else {
      return localDocumentsMap;
    }
  }, [localDocumentsMap, rootFolderEmpty]);

  return (
    <>
      {rootFolderVisible && !rootFolderEmpty ? (
        <div className="h-[5px]"></div>
      ) : (
        <Droppable droppableId={ROOT_FOLDER_PATH}>
          <div className="h-[20px]"></div>
        </Droppable>
      )}
      {Array.from(adjustedFiles.keys())
        .sort((a, b) => {
          // Add a space so that special characters don't break intuitive path ordering
          const newA = a.replaceAll('/', ' /');
          const newB = b.replaceAll('/', ' /');
          if (newA < newB) {
            return -1;
          } else if (newA > newB) {
            return 1;
          }
          return 0;
        })
        .map((path) => (
          <Droppable key={path} droppableId={path}>
            <OrganizedViewLayout
              path={path}
              files={adjustedFiles.get(path) || []}
              dataRoomInteraction={dataRoomInteraction}
            />
          </Droppable>
        ))}
    </>
  );
});
